import React, { useCallback, useEffect } from "react";

import { TextArea, TextAreaProps } from "@radix-ui/themes";
import { MarginProps } from "@radix-ui/themes/dist/cjs/props/margin.props";
import { styled } from "@stitches/react";

type TextareaProps = Omit<React.HTMLProps<HTMLTextAreaElement>, "size"> &
  MarginProps & {
    color?: TextAreaProps["color"];
    resize?: TextAreaProps["resize"];
  };

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (props: TextareaProps, ref: React.Ref<HTMLTextAreaElement>) => {
    // Ensure defaultValue and value are strings if they exist
    const { defaultValue, value, ...restProps } = props;
    const stringDefaultValue =
      defaultValue !== undefined ? String(defaultValue) : undefined;
    const stringValue = value !== undefined ? String(value) : undefined;

    return (
      <TextArea
        {...restProps}
        ref={ref}
        size={"2"}
        variant="surface"
        defaultValue={stringDefaultValue}
        value={stringValue}
      />
    );
  }
);

const MirroredTextAreaContainer = styled("div", {
  position: "relative",
  textarea: {
    background: "transparent",
    position: "relative",
    color: "transparent",
    caretColor: "var(--gray-12)",
  },
  "> .mirror": {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
    overflow: "hidden",
    pointerEvents: "none",
    scrollbarGutter: "unset",
  },
});

const StyledBasicTextArea = styled(TextArea, {
  scrollbarGutter: "unset",
});

export const MirroredTextArea = React.forwardRef<
  HTMLTextAreaElement,
  TextareaProps
>((props: TextareaProps, ref: React.Ref<HTMLTextAreaElement>) => {
  // Ensure defaultValue and value are strings if they exist
  const { defaultValue, value, ...restProps } = props;
  const stringDefaultValue =
    defaultValue !== undefined ? String(defaultValue) : undefined;
  const stringValue = value !== undefined ? String(value) : undefined;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const mirrorRef = React.useRef<HTMLDivElement>(null);

  const syncStylesToMirror = useCallback(
    (textarea: HTMLTextAreaElement, mirror: HTMLDivElement) => {
      const textAreaStyles = window.getComputedStyle(textarea);

      mirror.style.border = textAreaStyles.border;
      mirror.style.boxSizing = textAreaStyles.boxSizing;
      mirror.style.fontFamily = textAreaStyles.fontFamily;
      mirror.style.fontSize = textAreaStyles.fontSize;
      mirror.style.fontWeight = textAreaStyles.fontWeight;
      mirror.style.letterSpacing = textAreaStyles.letterSpacing;
      mirror.style.lineHeight = textAreaStyles.lineHeight;
      mirror.style.padding = textAreaStyles.padding;
      mirror.style.textDecoration = textAreaStyles.textDecoration;
      mirror.style.textIndent = textAreaStyles.textIndent;
      mirror.style.textTransform = textAreaStyles.textTransform;
      mirror.style.whiteSpace = textAreaStyles.whiteSpace;
      mirror.style.wordSpacing = textAreaStyles.wordSpacing;
      mirror.style.wordWrap = textAreaStyles.wordWrap;
      mirror.style.borderColor = "transparent";
    },
    []
  );

  const syncValueToMirror = useCallback(
    (textarea: HTMLTextAreaElement, mirror: HTMLDivElement) => {
      mirror.textContent = textarea.value;
    },
    []
  );

  const syncScroll = useCallback(
    (textarea: HTMLTextAreaElement, mirror: HTMLDivElement) => {
      mirror.scrollTop = textarea.scrollTop;
      mirror.scrollLeft = textarea.scrollLeft;
    },
    []
  );

  useEffect(() => {
    const container = containerRef.current;
    const mirror = mirrorRef.current;
    if (!container || !mirror) return;

    const textarea = container.querySelector("textarea");
    if (!textarea) return;

    const parseValue = (v?: string) => {
      if (!v) return 0;
      return v.endsWith("px") ? parseInt(v.slice(0, -2), 10) : 0;
    };
    const borderWidth = parseValue(
      window.getComputedStyle(textarea).borderWidth
    );

    syncStylesToMirror(textarea, mirror);
    syncValueToMirror(textarea, mirror);
    const resizeObserver = new ResizeObserver(() => {
      mirror.style.width = `${textarea.clientWidth + 2 * borderWidth}px`;
      mirror.style.height = `${textarea.clientHeight + 2 * borderWidth}px`;
    });
    resizeObserver.observe(textarea);

    const scrollHandler = () => syncScroll(textarea, mirror);
    const syncValues = () => syncValueToMirror(textarea, mirror);

    textarea.addEventListener("scroll", scrollHandler);
    textarea.addEventListener("input", syncValues);

    return () => {
      textarea.removeEventListener("scroll", scrollHandler);
      textarea.removeEventListener("input", syncValues);
      resizeObserver.disconnect();
    };
  }, [syncScroll, syncStylesToMirror, syncValueToMirror]);

  return (
    <MirroredTextAreaContainer ref={containerRef}>
      <div ref={mirrorRef} className="mirror">
        {stringValue}
      </div>
      <StyledBasicTextArea
        {...restProps}
        ref={ref}
        size={"2"}
        variant="classic"
        defaultValue={stringDefaultValue}
        value={stringValue}
      />
    </MirroredTextAreaContainer>
  );
});

export default Textarea;
